/**
 * What a plan answer says, in the board's own terms — one reading for every
 * place that draws or lists a Stand.
 *
 * Lifted out of `PlanningScreen` and `CaseRoute` (12.10.2026) when the print
 * documents (`/vorgang/:id/unterlagen`, WP-E2-4) became the third reader of
 * the same answer. The board, the parts list on screen and the PDF must say
 * the same thing about the same element; three copies of „where does a
 * console sit" would be the first thing to drift. Nothing here decides
 * anything: which joints exist, how an element is cut, what its blank is —
 * all of it is the answer's statement, this only projects and indexes it.
 */
import type { DemoConsoleMark, DemoPlacedElement, DemoRunResult } from "@/api";
import type { DemoOutline } from "@/lib/outline";
import {
  WALL_START,
  wallLengthMm,
  type CornerMark,
  type Cutout,
  type CutoutReference,
  type FacadeFeature,
  type Placement,
} from "@/lib/placement";

import type { ConsoleMark, CutWord } from "./PlanningBoard";

/**
 * What the answer says about one element's ends — the leans and the blank.
 *
 * `blankMm` is what the workshop cuts; it equals the wall stretch wherever no
 * mitre grew it. The two are separate figures on purpose: the mitre lies
 * across the corner square, not along the wall.
 */
export interface ElementCut {
  start: CutWord;
  end: CutWord;
  /** How far the element reaches past either end, in **millimetres** — the
   * board scales them. Named `…Px` on the board's own prop because that is
   * where they become pixels. */
  growsStartPx: number;
  growsEndPx: number;
  blankMm: number;
}

/**
 * What the answer settled on at one wall's end corner.
 *
 * `kind` is `null` while nobody has mitred it — there is then nothing to
 * settle. Both fields are read off the answer and worked out nowhere else:
 * whether a corner is re-entrant is geometry the backend owns (E-47 §3.1).
 */
export interface CornerFact {
  kind: "aussen" | "innen" | null;
  /**
   * What the corner *is*, whatever anybody has put on it — `null` where the
   * walk does not turn there.
   *
   * Its own field beside `kind`, which says how the corner is cut *now*: a
   * corner nobody has mitred has no cut and every shape, and the menu has to
   * say what filling it would do before anybody fills it. Read off the
   * answer's `cornerKinds`; an answer from before that field existed reads as
   * the convex corner, the same compatibility reading `form_runs` takes.
   */
  shape: "aussen" | "innen" | null;
  gainMm: number;
}

/**
 * What a cut is called in the quantity list — and in the printed parts list.
 *
 * Both mitres are the same cut to a workshop — 45 degrees. Which way it leans
 * is the drawing's business, not the saw's. An end at a chamfer's corner is
 * its own word (design 2026-10-18 §6.5): cut oblique, on whatever angle the
 * chamfer has, and never a mitre — nothing grows there.
 */
export const CUT_NAMES: Record<CutWord, string> = {
  gerade: "gerade",
  gehrung_aussen: "Gehrung",
  gehrung_innen: "Gehrung",
  schraeg: "schräg (Fase)",
};

/** Every bracket the answer addressed, by its own key — built fresh off
 * `result.consoleMarks` rather than kept as a second source of truth. */
export function indexConsoleMarks(marks: DemoConsoleMark[]): Map<string, DemoConsoleMark> {
  const byKey = new Map<string, DemoConsoleMark>();
  for (const mark of marks) byKey.set(mark.key, mark);
  return byKey;
}

/**
 * Which wall ends a run actually crosses — the corners that can take a
 * mitre — and what each of them is.
 *
 * Read off the answer's own `corners`, which states which joints sit on a
 * wall boundary (§4.1); the wall is then the one element `joint` sits on.
 * The board is not asked to work either out.
 */
export function cornerFactsOf(result: DemoRunResult | null): Map<number, CornerFact> {
  const byWall = new Map<number, CornerFact>();
  for (const run of result?.runs ?? []) {
    const elements = run.elements ?? [];
    const shapes = run.cornerKinds ?? [];
    for (const [at, joint] of (run.corners ?? []).entries()) {
      const element = elements[joint];
      if (!element) continue;
      const cut = element.cutEnd ?? "gerade";
      // Paired with `corners` by position. `undefined` is an answer from
      // before the field existed and reads as the convex corner; the empty
      // string is a corner that does not turn at all.
      const shape = shapes[at];
      // The **derived** blank, which is not the same as the one that won:
      // where somebody typed one, `displacedBlankMm` is what it pushed
      // aside, and that is the figure this corner grew. Reading the winning
      // blank instead showed 600 mm of growth on a corner that grew 300,
      // because a hand-typed 1.600 was standing in for a derived 1.300
      // (found in the browser, 27.08.2026).
      const derivedBlank = element.displacedBlankMm ?? element.blankLengthMm ?? element.lengthMm;
      byWall.set(element.wallIndex, {
        // Only a mitre settles a kind; an oblique cut at a chamfer is none.
        kind: cut === "gehrung_aussen" ? "aussen" : cut === "gehrung_innen" ? "innen" : null,
        shape: shape === "" ? null : shape === "innen" ? "innen" : "aussen",
        gainMm: derivedBlank - element.lengthMm,
      });
    }
  }
  return byWall;
}

/**
 * How each element is cut and what it is cut *to*, by placement id.
 *
 * Straight from the answer, and one map rather than two: the board draws
 * the leans, the list and the CSV print the blank, and all three are
 * statements about the same element made in one place.
 */
export function cutsOf(result: DemoRunResult | null): Map<string, ElementCut> {
  const byId = new Map<string, ElementCut>();
  for (const run of result?.runs ?? []) {
    for (const element of run.elements ?? []) {
      byId.set(element.placementId, {
        start: element.cutStart ?? "gerade",
        end: element.cutEnd ?? "gerade",
        growsStartPx: element.growsAtStartMm ?? 0,
        growsEndPx: element.growsAtEndMm ?? 0,
        blankMm: element.blankLengthMm ?? element.lengthMm,
      });
    }
  }
  return byId;
}

/**
 * The answer's own laid elements, by `placementId` — the parts list and the
 * CSV read a hole's figures off this, never off the draft (§11).
 */
export function elementsOf(result: DemoRunResult | null): Map<string, DemoPlacedElement> {
  const byId = new Map<string, DemoPlacedElement>();
  for (const run of result?.runs ?? []) {
    for (const element of run.elements ?? []) {
      byId.set(element.placementId, element);
    }
  }
  return byId;
}

/**
 * Console positions, projected from the runs.
 *
 * The answer states *which* joints there are and what each one means
 * (`corners`, `bridged`, `ring`); this turns them into millimetres. The
 * domain statement stays in the backend, the projection stays here — and
 * neither is made twice.
 *
 * Each mark is also addressed here (task 11) — the same key
 * `console_positions` computes on the backend, built the same way rather
 * than read off `result.consoleMarks` by position: the two lists are not
 * guaranteed to line up index for index (a mark the answer has nothing to
 * say about — an older answer, say — still has to sit somewhere on the
 * board), while the *key* is exact wherever both sides compute it.
 */
export function consoleMarksOf(
  outline: DemoOutline | null,
  result: DemoRunResult | null,
  marksByKey: ReadonlyMap<string, DemoConsoleMark>,
): ConsoleMark[] {
  if (!outline || !result) return [];
  const marks: ConsoleMark[] = [];

  for (const run of result.runs) {
    const elements = run.elements ?? [];
    if (elements.length === 0) continue;
    const corners = new Set(run.corners ?? []);
    const bridged = new Set(run.bridged ?? []);

    const at = (wallIndex: number, mm: number) => {
      const from = outline.nodes[wallIndex];
      const to = outline.nodes[wallIndex + 1];
      const length = wallLengthMm(outline, wallIndex);
      if (!from || !to || !length) return null;
      const fraction = mm / length;
      return {
        xMm: from.xMm + (to.xMm - from.xMm) * fraction,
        yMm: from.yMm + (to.yMm - from.yMm) * fraction,
      };
    };

    /** `ConsolePosition.key`/`DemoConsoleMark.key`, computed the same way
     * the backend does — never read off the answer by position (see the
     * comment above). */
    const addressed = (placementId: string, spot: "start" | "mitte" | "ende") => {
      const key = `${placementId}:${spot}`;
      const found = marksByKey.get(key);
      return { key, articleNo: found?.articleNo, proposedArticleNo: found?.proposedArticleNo };
    };

    /**
     * How many joints this run has — and it is not always `n − 1`.
     *
     * An open run of n elements has n−1 joints between them and two ends:
     * n+1 brackets. A **ring** has n joints and no ends, because the last
     * element also meets the first — `layout.py` hangs that seam joint on as
     * index `n−1`, and `GratingRun.consoles` counts it.
     *
     * The first version looped to `n−1` for both and then added the two ends
     * only for an open run, so a fully covered house drew **one bracket
     * fewer than the derivation printed above it** — and the missing one was
     * the shared corner at the seam, the largest mark on the board. Somebody
     * counting the picture against the list would have found them different
     * (found in review, 16.08.2026).
     */
    const joints = run.ring ? elements.length : elements.length - 1;
    for (let index = 0; index < joints; index += 1) {
      const element = elements[index]!;
      const point = at(element.wallIndex, element.offsetMm + element.lengthMm);
      if (!point) continue;
      // How wide the opening is that a bridged joint walks over. Same wall
      // only: across a corner there is no single stretch to span, and the
      // arc would be drawn over two directions at once.
      const after = elements[(index + 1) % elements.length];
      const spanMm =
        bridged.has(index) && after && after.wallIndex === element.wallIndex
          ? Math.max(after.offsetMm - (element.offsetMm + element.lengthMm), 0)
          : undefined;
      marks.push({
        ...point,
        kind: corners.has(index) ? "corner" : bridged.has(index) ? "bridged" : "joint",
        spanMm,
        ...addressed(element.placementId, "ende"),
      });
    }

    // A ring has no ends, so it gets none drawn.
    if (!run.ring) {
      const first = elements[0]!;
      const last = elements.at(-1)!;
      const head = at(first.wallIndex, first.offsetMm);
      const tail = at(last.wallIndex, last.offsetMm + last.lengthMm);
      if (head) marks.push({ ...head, kind: "joint", ...addressed(first.placementId, "start") });
      if (tail) marks.push({ ...tail, kind: "joint", ...addressed(last.placementId, "ende") });
    }

    // Which elements carry a middle bracket is a domain statement and comes
    // from `run.middles`, exactly as `corners` and `bridged` do. A board that
    // compared lengths itself would be a second opinion about the rule.
    for (const index of run.middles ?? []) {
      const element = elements[index];
      if (!element) continue;
      const point = at(element.wallIndex, element.offsetMm + element.lengthMm / 2);
      if (point) {
        marks.push({ ...point, kind: "middle", ...addressed(element.placementId, "mitte") });
      }
    }
  }
  return marks;
}

// ── The stored document, read back ──────────────────────────────────────

/**
 * The stored document, only as far as this file reads it — the shape
 * `document_to_json` writes (`schema/serialize.py`), which travels as
 * `DemoRunResult.documentJson`.
 *
 * Read structurally rather than typed against the generated schema: the
 * endpoint declares it as a free-form object, and the three lists wanted
 * here are ones the serialiser writes (`cutouts` since `schemaVersion` 2,
 * 12.09.2026 — a v1 document is lifted on the server, so it arrives with
 * the list too). The walls are **not** read from it — `wallIndexById` takes
 * them off the chosen outline instead.
 */
interface StoredDocument {
  corners?: {
    wallId?: unknown;
    treatment?: unknown;
    filledWallId?: unknown;
    kindOverride?: unknown;
    gainOverrideMm?: unknown;
  }[];
  features?: Record<string, unknown>[];
  cutouts?: Record<string, unknown>[];
}

/**
 * `wallId → wallIndex`, the one translation between the stored document and
 * the board.
 *
 * Read off the **chosen outline's own segments** — the very array
 * `PlanningScreen.wallIdFor` reads in the other direction (fix round 2). The
 * document's wall list would give the same answer, since the two are aligned
 * positionally by a `strict=True` zip on the server (`_document_walls_by_edge`),
 * but going through the segments means the two directions cannot drift apart:
 * whatever id the board would *post* for wall n is the id that put the mark on
 * wall n in the first place.
 *
 * A wall id that is not in the map is left out rather than landing on wall 0.
 * Where no outline is chosen there is no map and no marks — nothing can be
 * drawn on a board with no shape, and nothing could be written back either.
 */
function wallIndexById(outline: DemoOutline | null): Map<string, number> {
  const index = new Map<string, number>();
  outline?.segments.forEach((segment, position) => {
    if (typeof segment.wallId === "string") index.set(segment.wallId, position);
  });
  return index;
}

function isCornerTreatment(value: unknown): value is "gehrung" | "fuellung" {
  return value === "gehrung" || value === "fuellung";
}

function isCornerKind(value: unknown): value is "aussen" | "innen" {
  return value === "aussen" || value === "innen";
}

/** The corner marks of the stored document, in board terms. */
function storedCorners(document: StoredDocument | null, byId: Map<string, number>): CornerMark[] {
  const marks: CornerMark[] = [];
  for (const corner of document?.corners ?? []) {
    const wallIndex = typeof corner.wallId === "string" ? byId.get(corner.wallId) : undefined;
    if (wallIndex === undefined) continue;
    const filled = typeof corner.filledWallId === "string" ? byId.get(corner.filledWallId) : undefined;
    marks.push({
      wallIndex,
      treatment: isCornerTreatment(corner.treatment) ? corner.treatment : "gehrung",
      filledWallIndex: filled ?? null,
      kindOverride: isCornerKind(corner.kindOverride) ? corner.kindOverride : null,
      gainOverrideMm: typeof corner.gainOverrideMm === "number" ? corner.gainOverrideMm : null,
    });
  }
  return marks;
}

/** The facade objects of the stored document, in board terms. */
function storedFeatures(document: StoredDocument | null, byId: Map<string, number>): FacadeFeature[] {
  const features: FacadeFeature[] = [];
  for (const stored of document?.features ?? []) {
    const wallIndex = typeof stored.wallId === "string" ? byId.get(stored.wallId) : undefined;
    if (wallIndex === undefined || typeof stored.id !== "string") continue;
    features.push({
      ...(stored as unknown as FacadeFeature),
      id: stored.id,
      wallIndex,
    });
  }
  return features;
}

/** The holes of the stored document, in board terms — a hole on a wall the
 * chosen outline does not carry is dropped, as a corner would be. */
function storedCutouts(document: StoredDocument | null, byId: Map<string, number>): Cutout[] {
  const cutouts: Cutout[] = [];
  for (const stored of document?.cutouts ?? []) {
    const wallIndex = typeof stored.wallId === "string" ? byId.get(stored.wallId) : undefined;
    if (wallIndex === undefined || typeof stored.id !== "string") continue;
    const reference = (stored.reference ?? WALL_START) as CutoutReference;
    cutouts.push({ ...(stored as unknown as Cutout), id: stored.id, wallIndex, reference });
  }
  return cutouts;
}

/** Everything a Stand put on its shape, as the board holds it. */
export interface StoredDraft {
  placements: Placement[];
  corners: CornerMark[];
  features: FacadeFeature[];
  cutouts: Cutout[];
}

/**
 * Everything a stored version put on the shape, as the board holds it.
 *
 * Two sources and not one, because the server keeps them apart: the pieces
 * are `plan_version.planning_state`, the corners, the facade objects and the
 * holes are the document the ops log built (design §4.1). Neither is
 * derivable from the other.
 */
export function storedDraft(plan: DemoRunResult): StoredDraft {
  const document = (plan.documentJson ?? null) as StoredDocument | null;
  const byId = wallIndexById(chosenOutline(plan));
  return {
    placements: (plan.planningState?.placements ?? []) as unknown as Placement[],
    corners: storedCorners(document, byId),
    features: storedFeatures(document, byId),
    cutouts: storedCutouts(document, byId),
  };
}

/**
 * The finding that says a stored chamfer cannot be cut out of this Stand's
 * form (`fase_not_drawable`, design 2026-10-18, Task 4 ruling 1), or `null`.
 *
 * Where it stands the answer is a degraded one: its outline is the **uncut**
 * ring, its runs carry the laid elements moved back onto that ring by the
 * server, and the stored planning state still counts the walls of the cut
 * ring. Every reader that draws or lists elements has to know — the board,
 * the plan sheet and the parts list alike (review of Task 8, I-1).
 */
export function notDrawableFinding(plan: DemoRunResult | null) {
  return plan?.outlineFindings.find((finding) => finding.rule === "fase_not_drawable") ?? null;
}

/**
 * The gratings an answer laid, as placements — for a Stand whose stored
 * state does not fit its outline ({@link notDrawableFinding}). Only what the
 * answer states: where, how long, the number, the type where it is not the
 * job's. Nothing typed by hand travels in a run, so nothing of the kind is
 * invented here; interruptions, extra brackets and surrounds are not in a
 * run at all and are left out rather than put on the wrong wall.
 */
export function placementsOfAnswer(plan: DemoRunResult | null, jobType: string | null): Placement[] {
  return (plan?.runs ?? []).flatMap((run) =>
    (run.elements ?? []).map((element) => ({
      id: element.placementId,
      kind: "grating" as const,
      wallIndex: element.wallIndex,
      offsetMm: element.offsetMm,
      lengthMm: element.lengthMm,
      blankLengthMm: null,
      blankSnapshotMm: null,
      gratingType: element.gratingType === jobType ? null : element.gratingType,
      widthMm: null,
      splitsRun: false,
      label: element.label,
      depthMm: null,
    })),
  );
}

/**
 * The placements a stored Stand is **drawn and listed** with — the one rule
 * the plan sheet, the schema sheet and the parts list share with the board:
 * the stored planning state, except where the chamfer cannot be drawn; there
 * the server's own answer on the uncut ring ({@link placementsOfAnswer}).
 *
 * Not what a board is hydrated with: the stored state stays what was stored,
 * and the board, read-only in that case, draws the same answer through the
 * same function.
 */
export function drawnPlacements(plan: DemoRunResult): Placement[] {
  return notDrawableFinding(plan) !== null
    ? placementsOfAnswer(plan, plan.grating.type ?? null)
    : storedDraft(plan).placements;
}

/** The one outline a Stand stands on — `chosen` on a replay, `null` where
 * the answer carries none. */
export function chosenOutline(plan: DemoRunResult): DemoOutline | null {
  return plan.outlines.find((candidate) => candidate.chosen) ?? null;
}
